/**
 * utils/formatters.js — Locale-aware formatting utilities
 *
 * All date and number formatting goes through these helpers.
 * They read the active i18n language at call time and map it
 * to the correct Intl locale, so dates/numbers render in the
 * user's chosen language (FR / EN / AR).
 *
 * These are pure functions — NOT React hooks.
 * They work correctly because the calling components use
 * useTranslation(), which triggers re-render on language change.
 */

import i18n from '../i18n';

// ── Locale map (i18n code → Intl locale) ────────────────────────────
const LOCALE_MAP = {
  fr: 'fr-FR',
  en: 'en-US',
  ar: 'ar-MA',
};

/** Current Intl locale based on active i18n language */
function getLocale() {
  const lang = (i18n.language || 'fr').substring(0, 2);
  return LOCALE_MAP[lang] || 'fr-FR';
}

// ── Date formatters ─────────────────────────────────────────────────

/**
 * Short date: "13 mars" / "Mar 13" / "13 مارس"
 * Use for compact date displays (tables, badges, compliance dots).
 */
export function formatShortDate(dateStr) {
  if (!dateStr) return '—';
  const dt = new Date(dateStr);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString(getLocale(), { day: 'numeric', month: 'short' });
}

/**
 * Medium date: "13 mars 2026" / "Mar 13, 2026" / "13 مارس 2026"
 * Use for dates that need the year (fines, reports).
 */
export function formatDate(dateStr) {
  if (!dateStr) return '—';
  const dt = new Date(dateStr);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString(getLocale(), { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Long date: "jeudi 13 mars 2026" / "Thursday, March 13, 2026" / "الخميس، 13 مارس 2026"
 * Use for the Dashboard subtitle (today's date).
 */
export function formatLongDate(date) {
  if (!date) return '—';
  const dt = date instanceof Date ? date : new Date(date);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString(getLocale(), {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

// ── Number formatters ───────────────────────────────────────────────

/**
 * Locale-aware number: "2 800" / "2,800" / "٢٬٨٠٠"
 * Use for mileage, amounts, counts that need thousand separators.
 */
export function formatNumber(n) {
  if (n == null || isNaN(n)) return '—';
  return Number(n).toLocaleString(getLocale());
}

/**
 * Locale-aware time: "14:30" / "2:30 PM" / "٢:٣٠ م"
 * Use for transaction timestamps and schedule displays.
 */
export function formatTime(dateStr) {
  if (!dateStr) return '—';
  const dt = new Date(dateStr);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit' });
}

/**
 * Locale-aware absolute amount: "2 800" / "2,800" / "٢٬٨٠٠"
 * Handles negative amounts with a leading "- " prefix.
 * Use for MAD currency displays (without the "MAD" suffix).
 */
export function formatMAD(amount) {
  if (amount == null) return '—';
  const n = Number(amount);
  const prefix = n < 0 ? '- ' : '';
  return prefix + Math.abs(n).toLocaleString(getLocale());
}
